import { useState } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Box } from '@mui/material'
import { useSelector } from 'react-redux'

import Layout from './components/Layout'
import LoginPage from './pages/LoginPage'
import SignupPage from './pages/SignupPage'
import DashboardPage from './pages/DashboardPage'
import AnalysesPage from './pages/AnalysesPage'
import AnalysisDetailPage from './pages/AnalysisDetailPage'
// CreateAnalysisPage hidden — analyses come from scanner scans
// import CreateAnalysisPage from './pages/CreateAnalysisPage'
import ReportsPage from './pages/ReportsPage'
import TenantsPage from './pages/TenantsPage'
import ScannersPage from './pages/ScannersPage'
import KnowledgeBasePage from './pages/KnowledgeBasePage'
import AnalyticsPage from './pages/AnalyticsPage'
import AdminPage from './pages/AdminPage'
import SettingsPage from './pages/SettingsPage'
import CertificatesPage from './pages/CertificatesPage'
import AgentDriftDetailPage from './pages/AgentDriftDetailPage'
import PricingPage from './pages/PricingPage'
import BillingPage from './pages/BillingPage'
import TeamPage from './pages/TeamPage'
import DocsPage from './pages/DocsPage'
import CustomRulesPage from './pages/CustomRulesPage'
import IntegrationsPage from './pages/IntegrationsPage'
import PrivacyPage from './pages/PrivacyPage'
import TermsPage from './pages/TermsPage'
import VerifyEmailPage from './pages/VerifyEmailPage'
import AgentRegistryPage from './pages/AgentRegistryPage'
import CompliancePosturePage from './pages/CompliancePosturePage'
import CostAnalysisPage from './pages/CostAnalysisPage'
import TopologyPage from './pages/TopologyPage'
import ScanDiffPage from './pages/ScanDiffPage'
import RequestDemoPage from './pages/RequestDemoPage'
import { RootState } from './store'

function App() {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth)
  const [showSignup, setShowSignup] = useState(false)

  // Public pages — accessible without login
  if (window.location.pathname === '/docs') return <DocsPage />
  if (window.location.pathname === '/privacy') return <PrivacyPage />
  if (window.location.pathname === '/terms') return <TermsPage />
  if (window.location.pathname === '/verify-email') return <VerifyEmailPage />
  // Prospects have no account, so this must render before the auth gate.
  if (window.location.pathname === '/request-demo') return <RequestDemoPage />

  if (!isAuthenticated) {
    return showSignup
      ? <SignupPage onSwitchToLogin={() => setShowSignup(false)} />
      : <LoginPage onSwitchToSignup={() => setShowSignup(true)} />
  }

  const isAdmin = user?.type === 'admin'

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Layout>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/agents" element={<AgentRegistryPage />} />
          <Route path="/analyses" element={<AnalysesPage />} />
          {/* /analyses/new hidden — analyses come from scanner scans */}
          <Route path="/analyses/:analysisId" element={<AnalysisDetailPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/scanners" element={<ScannersPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/certificates" element={<CertificatesPage />} />
          {/* P1b — per-cert drift timeline + approve action */}
          <Route path="/certificates/:certId" element={<AgentDriftDetailPage />} />
          <Route path="/compliance" element={<CompliancePosturePage />} />
          {/* Frameworks moved into Custom Rules, where the other two sources
              of a tenant rule already lived. Kept as a redirect for one
              release so an existing bookmark still lands somewhere useful. */}
          <Route path="/frameworks" element={<Navigate to="/custom-rules?tab=frameworks" replace />} />
          <Route path="/cost" element={<CostAnalysisPage />} />
          <Route path="/topology" element={<TopologyPage />} />
          {/* SD2 — Scan Comparison (Release Diff). 404s when FEATURE_RELEASE_DIFF=false. */}
          <Route path="/diff/:repoIdentityHash" element={<ScanDiffPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/custom-rules" element={<CustomRulesPage />} />
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/docs" element={<DocsPage />} />
          {/* Admin-only routes */}
          {isAdmin ? (
            <>
              <Route path="/tenants" element={<TenantsPage />} />
              <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/admin" element={<AdminPage />} />
            </>
          ) : null}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Layout>
    </Box>
  )
}

export default App
